import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateConstant } from 'store/actions/constantAction';
import axios, { api } from 'config/axios';
import Classes from 'Api/Classes';

const SETTINGS_TEMPLATE = (existing) => {
    return {
        checkin: {
            comments: {
                title: 'Enable student context options for check-in responses',
                enabled: true,
                subsettings: {
                    lessons: {
                        title: 'When a student is logged in',
                        enabled: existing?.checkin?.comments?.lessons || existing?.WeatherReportComments,
                    },
                    rollcall: {
                        title: 'In the Class Check-in',
                        enabled: existing?.checkin?.comments?.rollcall,
                    },
                    prepare_for_learning: {
                        title: 'During a Prepare for Learning',
                        enabled: existing?.checkin?.comments?.prepare_for_learning,
                    },
                },
            },
            checkin_before_lesson: {
                title: 'Carry out a check-in before each lesson',
                enabled: existing?.checkin?.checkin_before_lesson || existing.LessonWeatherReport,
            },
            learning_support: {
                title: 'Foundation for Emotional Literacy Mode',
                enabled: true,
                subsettings: {
                    prepare_for_learning: {
                        title: 'During a Prepare for Learning',
                        enabled: existing?.checkin?.learning_support.prepare_for_learning || existing?.LearningSupport,
                    },
                    student_override: {
                        title: 'Turn on for all students (does not affect individual student setting)',
                        enabled: existing?.checkin?.learning_support?.student_override,
                    },
                },
            },
            class_checkin: {
                title: 'Validate Class Checkin',
                enabled: true,
                subsettings: {
                    validate: {
                        title: 'Require a student username or shortcode for class checkin responses',
                        enabled: existing?.checkin?.class_checkin?.validate,
                    },
                },
            },
            images: {
                title: 'Check-in Illustrations',
                enabled: true,
                subsettings: {
                    illustrations: {
                        title: 'Enable this setting to use character illustrations for the check-in. Disable this setting to use the Emoji style check-in.',
                        enabled: existing?.checkin?.images?.illustrations,
                    },
                },
            },
        },
        students: {
            student_management_dashboard: {
                title: 'Use our new student dashboard with bulk editing, selection and download of student details.',
                enabled: existing?.students?.student_management_dashboard,
            },
        },
        dashboard: {
            daily_summary: {
                enabled: existing?.dashboard?.daily_summary !== undefined ? existing?.dashboard?.daily_summary : true,
            },
            comments_summary_graph_view: {
                enabled:
                    existing?.dashboard?.comments_summary_graph_view !== undefined
                        ? existing?.dashboard?.comments_summary_graph_view
                        : false,
            },
        },
    };
};

const useClassSettings = (classId, subsetting) => {
    const classSettings = useSelector((state) => state.constants.classSettings);
    const dispatch = useDispatch();
    const [settings, setSettings] = useState({ checkin: {}, dashboard: {} });

    useEffect(() => {
        const fetchData = async () => {
            if (classSettings?.[classId]) {
                // console.log(classSettings[classId]);
                setSettings(classSettings[classId]);
            } else {
                if (classId) {
                    const response = await Classes.getClassSettings({ classId });
                    const template = SETTINGS_TEMPLATE(response.classSettings);
                    console.log(response, template);
                    const settings = {
                        checkin: template.checkin,
                        dashboard: template.dashboard,
                    };
                    dispatch(updateConstant('classSettings', settings, classId));
                    setSettings(settings);
                }
            }
        };

        fetchData();
    }, [classSettings, classId, dispatch]);

    const saveSettings = (settings) => {
        const saveTemplate = {
            checkin: {
                comments: {
                    lessons: settings.checkin.comments.subsettings.lessons.enabled,
                    rollcall: settings.checkin.comments.subsettings.rollcall.enabled,
                    prepare_for_learning: settings.checkin.comments.subsettings.prepare_for_learning.enabled,
                },
                checkin_before_lesson: settings.checkin.checkin_before_lesson.enabled,
                learning_support: {
                    prepare_for_learning: settings.checkin.learning_support.subsettings.prepare_for_learning.enabled,
                    student_override: settings.checkin.learning_support.subsettings.student_override.enabled,
                },
                class_checkin: {
                    validate: settings.checkin.class_checkin.subsettings.validate.enabled,
                },
                images: {
                    illustrations: settings.checkin.images.subsettings.illustrations.enabled,
                },
            },
            students: {
                student_management_dashboard: settings?.students?.student_management_dashboard?.enabled,
            },
            dashboard: {
                daily_summary: settings?.dashboard?.daily_summary?.enabled,
                comments_summary_graph_view: settings?.dashboard?.comments_summary_graph_view?.enabled,
            },
        };
        Classes.updateClassSettings({ classId, settings: saveTemplate }).then((newClassSetting) => {
            const newSettingObject = SETTINGS_TEMPLATE(newClassSetting);
            dispatch(updateConstant('classSettings', newSettingObject));
        });
    };

    return [settings, saveSettings];
};

export default useClassSettings;
